import { useTranslation } from "react-i18next";

import {
  type AwaitingQuestionLine,
  formatAwaitingQuestions,
} from "./awaiting-questions";

/**
 * The READ-ONLY question list inside a parked mission's / harness's
 * awaiting-user block.
 *
 * ⚠ THIS MUST NOT LOOK ANSWERABLE, and that is a ruling, not a style
 * choice. Answering an `awaiting_user` fork stays CHAT-MEDIATED: the
 * concierge holds the question and calls `lx_mission_resume`, because a
 * pane-side resume POST bypasses the persona's approval gating for
 * irreversible / court-facing acts (handoff
 * `lexide-team-agents-missions-surface-handoff-2026-06-22.md` §5, still
 * ruled 2026-08-17). So this renders TEXT — no button, no chip, no
 * hover/pointer affordance, no role. A disabled chip would invite a click
 * that does nothing, which is this lane's live A-4 defect shape (an
 * upload control that renders and silently no-ops).
 *
 * Rendering rules it inherits from `formatAwaitingQuestions`:
 * every entry is shown (count in === count out), and one that could not
 * be read is marked visibly rather than blanked — a blank line would read
 * as a question with no text, the reassuring direction.
 */
export function AwaitingQuestionsList({
  questions,
}: {
  questions: readonly unknown[] | undefined;
}) {
  const { t } = useTranslation();

  // ⚠ ABSENT ≠ EMPTY, and collapsing them here would be this lane's
  // `unknown-must-be-representable` violation. `undefined` means we were
  // never served the field (an older record, or a list endpoint that does
  // not carry it) — we could not LOOK, so we say nothing. `[]` means the
  // service told us there are none, which is a real answer and keeps the
  // count line the harness block has always shown.
  if (!Array.isArray(questions)) return null;

  const lines = formatAwaitingQuestions(questions);

  return (
    <>
      <p className="mt-1 text-amber-800/90 dark:text-amber-300/80">
        {t("detailDialog.awaitingUserQuestions", { count: lines.length })}
      </p>
      {lines.length === 0 ? null : (
      <ul
        data-testid="awaiting-user-questions"
        className="mt-1 list-disc space-y-1 pl-4 text-amber-900/90 dark:text-amber-200/90"
      >
        {lines.map((line) => (
          <QuestionLine key={line.key} line={line} />
        ))}
      </ul>
      )}
    </>
  );
}

function QuestionLine({ line }: { line: AwaitingQuestionLine }) {
  const { t } = useTranslation();
  if (line.unreadable) {
    return (
      <li data-testid="awaiting-user-question-unreadable" className="italic opacity-80">
        {t("detailDialog.awaitingUserUnreadable")}{" "}
        <code className="not-italic break-all">{line.text}</code>
      </li>
    );
  }
  return (
    <li data-testid="awaiting-user-question" className="break-words">
      {line.text}
    </li>
  );
}
